import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router';
import { useService } from '../../../services/services';
import { useObservableState } from 'observable-hooks';
import { useTranslation } from 'react-i18next';
import { map } from 'rxjs/operators';
import { useQuery } from '../../../hooks/use-query';

import OptionModal from '../../../components/modals/option-modal';
import Link, { LinkComponent } from '../../../components/form/elements/link';
import Button from '../../../components/form/elements/button';
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
  useTab,
} from '../../../components/common/tabs/tab-container';

import css from './view.module.css';

import {
  CROP_CHANGE_PLAN_INDEX,
  CROP_CHANGE_PLAN_EDIT,
  CROP_CHANGE_PLAN_VIEW,
  CROP_FIELDS_AND_WORKS_INDEX,
  CROP_FERTILIZATION_PLAN_VIEW,
} from '../../../router/routes';
import { can, objectProperty, route } from '../../../services/utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Section } from '../fields-and-works';
import { CropWorkType } from '../../../enums/crop-work-types';

export default function CropChangePlanView() {
  const { cropChangePlanService } = useService();
  const history = useHistory();
  const [params] = useQuery();
  const { farmId, season, id } = useParams<{
    farmId: string;
    season: string;
    id: string;
  }>();
  const [modal, setModal] = useState(null);
  const [footer, setFooter] = useState([]);
  const [title, setTitle] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [currentCropChangePlan, setCurrentCropChangePlan] = useState(null);

  const { t } = useTranslation();
  const transKeyPrefix = 'pages.crop.crop_change_plan.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const seasonNumber = parseInt(season);

  const [allFieldInfo] = useObservableState<any>(
    () =>
      cropChangePlanService
        .showStatic(farmId, id, { fieldIds: params.fieldIds?.split(',') })
        .pipe(
          map((fieldInfo: any) => {
            setSelectedField(fieldInfo[0]);
            return fieldInfo;
          })
        ),
    []
  );

  const [currentTab, setTab] = useTab(CROP_CHANGE_PLAN_VIEW, [
    'id',
    'farmId',
    'season',
  ]);

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const loadValues = useCallback(
    (value) => {
      if (selectedField?.cropChangePlans) {
        return Object.entries(selectedField.cropChangePlans).map(
          ([seasonId, cropChangePlan]: any) => {
            if (
              cropChangePlan?.crop_id ===
                cropChangePlan?.interculture_crop_id &&
              (value === 'crop_value' || value === 'breed')
            ) {
              return (
                <td key={`${seasonId}`} className={css['values']}>
                  {'-'}
                </td>
              );
            } else {
              return (
                <td key={`${seasonId}`} className={css['values']}>
                  {`${objectProperty(cropChangePlan, value) ?? '-'}`}
                </td>
              );
            }
          }
        );
      }
    },
    [selectedField]
  );

  const loadProductionTypes = useCallback(() => {
    function productionYield(row: number) {
      let contents = [];

      for (let seasonIndex = 3; seasonIndex >= 0; seasonIndex--) {
        const usedSeason = seasonNumber - seasonIndex;
        const prodTypeInfo =
          selectedField.cropChangePlans[usedSeason]?.production_type_info?.[
            row
          ];
        let prodTypeInfoLabel = '-';

        if (prodTypeInfo) {
          prodTypeInfoLabel =
            prodTypeInfo.type_name +
            ' ' +
            prodTypeInfo.yield +
            prodTypeInfo.yield_unit;
        }

        contents.push(
          <td
            key={`${usedSeason}`}
            className={css['values']}
          >{`${prodTypeInfoLabel}`}</td>
        );
      }

      return contents;
    }

    if (selectedField?.cropChangePlans) {
      const label = tp('edit.yield');
      let maxRows = 1;
      let tableContents = [];

      Object.values(selectedField.cropChangePlans).forEach(
        (cropChangePlan: any) => {
          if (cropChangePlan?.production_type_info?.length > maxRows) {
            maxRows = cropChangePlan?.production_type_info.length;
          }
        }
      );

      for (let row = 0; row < maxRows; row++) {
        tableContents.push(
          <tr key={`yield.${row}`}>
            <td className={css['label']}>{row === 0 ? label : ''}</td>
            {productionYield(row)}
          </tr>
        );
      }

      return tableContents;
    }
  }, [tp, seasonNumber, selectedField]);

  const fertilizationPlanExistsWarningModal = useCallback(
    (url: string) =>
      setModal(
        <OptionModal
          title={tp('fertilization-plan-constraints-exist')}
          content={tp('delete-fertilization-plan-first')}
          buttons={[
            { title: tp('goto-fertilization-plan'), name: 'yes', value: true },
            { title: t('cancel'), name: 'no', value: false },
          ]}
          onChoice={(choice) => {
            if (choice) {
              window.open(url, '_blank', 'noreferrer');
              setModal(null);
            } else {
              setModal(null);
            }
          }}
          onClose={() => setModal(null)}
        />
      ),
    [t, tp]
  );

  const doneWorksExistsWarningModal = useCallback(
    (url: string) =>
      setModal(
        <OptionModal
          title={tp('works-constraints-exist')}
          content={tp('delete-works-first')}
          buttons={[
            { title: tp('goto-works'), name: 'yes', value: true },
            { title: t('cancel'), name: 'no', value: false },
          ]}
          onChoice={(choice) => {
            if (choice) {
              window.open(url, '_blank', 'noreferrer');
              setModal(null);
            } else {
              setModal(null);
            }
          }}
          onClose={() => setModal(null)}
        />
      ),
    [t, tp]
  );

  const deleteCropChangePlanModal = useCallback(() => {
    return (
      <OptionModal
        title={tp('delete')}
        content={tp('confirm_delete')}
        buttons={[
          { title: t('yes'), name: 'yes', value: true },
          { title: t('no'), name: 'no', value: false },
        ]}
        onChoice={(choice) => {
          if (choice) {
            cropChangePlanService.destroy(farmId, id).subscribe(
              () => {
                history.push(
                  route(CROP_CHANGE_PLAN_INDEX, {
                    farmId: farmId,
                    season: season,
                  })
                );
              },
              (error) => {
                if (error.status === 422) {
                  switch (true) {
                    case error.response.hasOwnProperty(
                      'fertilization_plan_exists'
                    ):
                      fertilizationPlanExistsWarningModal(
                        route(CROP_FERTILIZATION_PLAN_VIEW, {
                          farmId: farmId,
                          season: season,
                          id: error.response.fertilization_plan_exists,
                        })
                      );
                      break;
                    case error.response.hasOwnProperty('done_works_exist'):
                      doneWorksExistsWarningModal(
                        route(
                          CROP_FIELDS_AND_WORKS_INDEX.concat(
                            '?type=:type&field_name=:field&done=true'
                          ),
                          {
                            farmId: farmId,
                            season: season,
                            tab: Section.workOverview,
                            type: CropWorkType.SOWING_OR_PLANTING,
                            field: error.response.done_works_exist,
                          }
                        )
                      );
                      break;
                  }
                }
              }
            );
          } else {
            setModal(null);
          }
        }}
        onClose={() => setModal(null)}
      />
    );
  }, [
    t,
    tp,
    cropChangePlanService,
    farmId,
    id,
    history,
    season,
    doneWorksExistsWarningModal,
    fertilizationPlanExistsWarningModal,
  ]);

  const destroy = useCallback(() => {
    setModal(deleteCropChangePlanModal);
  }, [deleteCropChangePlanModal]);

  useEffect(() => {
    if (currentTab) {
      setSelectedField(
        allFieldInfo.find(
          (fieldInfo: any) =>
            fieldInfo.cropChangePlans[seasonNumber].id === currentTab
        )
      );

      if (selectedField) {
        setCurrentCropChangePlan(selectedField.cropChangePlans?.[seasonNumber]);
      }
    }

    if (currentCropChangePlan) {
      setTitle(`${currentCropChangePlan?.field.name ?? ''} ${seasonNumber}`);

      setFooter(() => {
        let elements = [];

        if (can(farmUser, { title: 'update crop_change_plan' })) {
          elements.push(
            Link(
              route(CROP_CHANGE_PLAN_EDIT, { farmId, season, id }),
              'edit',
              true
            )
          );
        }

        if (can(farmUser, { title: 'delete crop_change_plan' })) {
          elements.push(Button('delete', destroy));
        }

        return elements;
      });
    }
  }, [
    destroy,
    farmId,
    farmUser,
    season,
    seasonNumber,
    id,
    selectedField,
    currentTab,
    allFieldInfo,
    currentCropChangePlan,
  ]);

  return (
    <div className={css['crop-change-plan-table']}>
      <div className={css['container']}>
        <div className={css['wrapper']}>
          <div className={css['header']}>
            <LinkComponent
              label="back"
              icon={faLongArrowLeft}
              linkTo={route(CROP_CHANGE_PLAN_INDEX, { farmId, season })}
            />
            <h2 className={css['title']}>{title}</h2>
          </div>
          <div className={css['table-wrapper']}>
            <TabContainer
              selectedTabKey={currentTab}
              setTab={setTab}
              tabNavigation={
                <TabNavigation>
                  {allFieldInfo?.map((fieldInfo: any) => {
                    const currentPlan =
                      fieldInfo.cropChangePlans?.[seasonNumber];
                    return (
                      <TabLink
                        key={currentPlan?.id ?? id}
                        tabKey={currentPlan?.id}
                      >
                        {currentPlan.field.lad_number +
                          ' ' +
                          currentPlan.field.name +
                          ' ' +
                          currentPlan.field.season_id}
                      </TabLink>
                    );
                  })}
                </TabNavigation>
              }
              tabContents={
                <TabContents>
                  {allFieldInfo?.map((fieldInfo: any) => {
                    const currentPlan =
                      fieldInfo.cropChangePlans?.[seasonNumber];
                    return (
                      <Tab key={currentPlan?.id} tabKey={currentPlan?.id}>
                        <table className={css['table']}>
                          <thead>
                            <tr>
                              <th></th>
                              <th>{seasonNumber - 3}</th>
                              <th>{seasonNumber - 2}</th>
                              <th>{seasonNumber - 1}</th>
                              <th className={css['current-season']}>
                                {season}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className={css['label']}>
                                {tp('create.field_names')}
                              </td>
                              {loadValues('field.name')}
                            </tr>
                            <tr>
                              <td className={css['label']}>
                                {tp('create.area')}
                              </td>
                              {loadValues('field.area')}
                            </tr>
                            <tr>
                              <td className={css['label']}>
                                {tp('edit.crop_name')}
                              </td>
                              {loadValues('crop_value')}
                            </tr>
                            <tr>
                              <td className={css['label']}>
                                {tp('edit.breed')}
                              </td>
                              {loadValues('breed')}
                            </tr>
                            {loadProductionTypes()}
                            <tr>
                              <td className={css['label']}>
                                {tp('edit.intercrop')}
                              </td>
                              {loadValues('intercrop_crop_value')}
                            </tr>
                            <tr>
                              <td className={css['label']}>
                                {tp('edit.interculture')}
                              </td>
                              {loadValues('interculture_crop_value')}
                            </tr>
                            <tr>
                              <td className={css['label']}>
                                {tp('create.notes')}
                              </td>
                              {loadValues('notes')}
                            </tr>
                          </tbody>
                        </table>
                      </Tab>
                    );
                  })}
                </TabContents>
              }
            />
            {footer && (
              <div className={css['footer']}>
                {footer.map((Child: any, index) => {
                  return <Child key={`${index}_footer_btn`} />;
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      {modal}
    </div>
  );
}
